import React, { useEffect, useState } from "react";
import {
  handleSelectChange,
} from "../../helpers/form";
import HelpText from "../tags/HelpText";
import {
  ExpandableRadioGroup,
  ExpandableRadioButton,
} from "../tags/ExpandableRadioGroup";
import ParentEducationLevel from "../tags/ParentEducationLevel";
import {
  getReferences,
  getVisaClassDetails,
  getVisaSubclassDetails,
} from "../../services/vu";
import Country from "./Country";
import Language from "./Language";
import CountryEnum from "../../enums/Country";

import {
  getHelperTextForPage,
  getTooltipsForPage,
} from "../../services/reservoir";

import { getTooltipBySlug } from "../../helpers/form";
import { getAttributeCourseStaticInfo } from "../../helpers/enrolment";
import parse from 'html-react-parser';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import Loading from "./Loading";
import * as Yup from 'yup';
import {
  isNotEmpty,
  isNotEmptyArray,
  isValidResidencyPostcodeYear12,
  isValidYearOfLeavingSchool,
  isValidYearOfArrival,
  isDisabilityNotSelected,
  isVisaQuestionNotSelected,
  isValidPermanentCountry
} from '../../helpers/validation'

const isVisaRequired = value => (value) === true;
const isVisaQuestionResponseY = (value) => value ===  'Y';
const isDisabilityIndicatorY = (value) => value === 'Y';
const isCountryOfBirthNotAustralia = (value) => {
  return value !== Country.Australia;
}
const isEnglishOnlyAtHomeN = (value) => value !== 'Y';
const isYear12InAustraliaN = (value) => value === 'N';
const isYear12InAustraliaY = (value) => value === 'Y';
const isSecondaryEducationCompleted2 = (value) => value === '2';

const AdditionalDetailsForm = props => {

  const validationSchema = Yup.object().shape({
    visaQuestionResponse: Yup.string()
      .when('isVisaRequired', {
        is: (value) => isVisaRequired(value),
        then: (schema) => schema.test('isVisaQuestionNotSelected', 'Please select your visa situation.', (value, context) => isVisaQuestionNotSelected(value, context))
      }),
    visaType: Yup.string()
      .when('visaQuestionResponse', {
        is: (value) => isVisaQuestionResponseY(value),
        then: (schema) => {
          return schema.test('isNotEmpty', 'Please provide your visa class.', (value, context) => isNotEmpty(value))
        }
      }),
    visaSubType: Yup.string()
      .when('visaQuestionResponse', {
        is: (value) => isVisaQuestionResponseY(value),
        then: (schema) => {
          return schema.test('isNotEmpty', 'Please provide your visa subclass.', (value, context) => isNotEmpty(value))
        }
      }),
    disabilityIndicator: Yup.string()
      .test('isNotEmpty', 'Please select whether you have a disability.', (value, context) => isNotEmpty(value))
      .test('isDisabilityNotSelected', 'Please select whether you have a disability.', (value, context) => isDisabilityNotSelected(value)),
    disability: Yup.array()
      .of(Yup.string())
      .when('disabilityIndicator', {
        is: (value) => isDisabilityIndicatorY(value),
        then: (schema) => {
          return schema.test('isNotEmptyArray', 'Please select one or more disabilities.', (value, context) => isNotEmptyArray(value))
        }
      }),
    contactInd: Yup.string()
      .when('disabilityIndicator', {
        is: (value) => isDisabilityIndicatorY(value),
        then: (schema) => {
          return schema.test('isNotEmpty', 'Please select whether you want to be contacted regarding your disability.', (value, context) => isNotEmpty(value))
        }
      }),
    countryOfBirth: Yup.string()
      .test('countryOfBirth_isNotEmpty', 'Please provide a country of birth.', (value, context) => {
        return isNotEmpty(value)
      })
      .test('countryOfBirth_isValidPermanentCountry', 'Please enter a valid country.', (value, context) => {
        return isValidPermanentCountry(value, props.countryCodes);
      }),
    yearOfArrival: Yup.string()
      .when('countryOfBirth', {
        is: (value) => isCountryOfBirthNotAustralia(value),
        then: (schema) => schema.test('isValidYearOfArrival', 'Arrival year must be between your date of birth and the current date.', (value, context) => isValidYearOfArrival(value, context))
      }),
    englishOnlyAtHome: Yup.string()
      .test('isNotEmpty', 'Please specify the language that you speak at home.', (value, context) => isNotEmpty(value)),
    language: Yup.string()
      .when('englishOnlyAtHome', {
        is: (value) => {
          return isEnglishOnlyAtHomeN(value)
        },
        then: (schema) => schema.test('isNotEmpty', 'Please select a language.', (value, context) => {
          return isNotEmpty(value)
        })
      }),
    year12InAustralia: Yup.string()
      .test('isNotEmpty', 'Please specify your permanent home residence during Year 12.', (value, context) => isNotEmpty(value)),
    countryOfResidenceYear12: Yup.string()
      .when('year12InAustralia', {
        is: (value) => {
          return isYear12InAustraliaN(value)
        },
        then: (schema) => schema.test('isNotEmpty', 'Please select a permanent home residence for Year 12.', (value, context) => {
          return isNotEmpty(value)
        })
      }),
    residencyPostcodeYear12: Yup.string()
      .test(
        'isValidResidencyPostcodeYear12',
        'Postcode must be valid.',
        (value, context) => isValidResidencyPostcodeYear12(value, context)
      ),
    residencySuburbYear12: Yup.string()
      .when('year12InAustralia', {
        is: (value) => {
          return isYear12InAustraliaY(value)
        },
        then: (schema) => schema.test('isNotEmpty', 'Please provide your suburb.', (value, context) => {
          return isNotEmpty(value)
        })
      }),
    parentGuardian1EduLvl: Yup.string()
      .test('isNotEmpty', 'Please provide the highest level of education by your parent.', (value, context) => isNotEmpty(value)),
    highestPriorEducationAchieved: Yup.string()
      .test('isNotEmpty', 'Please select your prior highest education level achieved.', (value, context) => isNotEmpty(value)),
    levelLeftSchool: Yup.string()
      .when('isSecondaryEducationCompleted', {
        is: (value) => {
          return isSecondaryEducationCompleted2(value)
        },
        then: (schema) => schema.test('isNotEmpty', 'Please select your prior highest education level achieved.', (value, context) => {
          return isNotEmpty(value)
        })
      }),
    isSecondaryEducationCompleted: Yup.string()
      .test('isNotEmpty', 'Please select whether you have completed your secondary education.', (value, context) => isNotEmpty(value)),
    yearLeftSchool: Yup.string()
      .when('isSecondaryEducationCompleted', {
        is: (value) => {
          return isSecondaryEducationCompleted2(value)
        },
        then: (schema) => schema.test('isNotEmpty', 'Please provide the last year you attended Secondary School', (value, context) => {
          return isNotEmpty(value)
        })
      })
      .when('isSecondaryEducationCompleted', {
        is: (value) => {
          return isSecondaryEducationCompleted2(value)
        },
        then: (schema) => schema.test('isValidYearOfLeavingSchool', 'Year must be valid.', (value, context) => {
          return isValidYearOfLeavingSchool(value, context)
        })
      }),
  });

  const [disabilities, setDisabilities] = useState([])
  const [highestPriorEducationList, setHighestPriorEducationList] = useState([])
  const [schoolLevelList, setSchoolLevelList] = useState([])
  const [indigenousCodesList, setInigenousCodesList] = useState([])
  const [citizenship, setCitizenship] = useState('')
  const [tooltips, setTooltips] = useState([])
  const [loading, setLoading] = useState(true)
  const [topCallout, setTopCallout] = useState({})
  const [visaTypeList, setVisaTypeList] = useState([])
  const [visaSubTypeList, setVisaSubTypeList] = useState([])
  const [courseLocationCd, setCourseLocationCd] = useState('')
  const [offshoreFlag, setOffshoreFlag] = useState('')
  const [form, setForm] = useState({
    disabilityIndicator: '',
    disability: [],
    contactInd: '',
    countryOfBirth: '',
    yearOfArrival: '',
    englishOnlyAtHome: '',
    language: '',
    year12InAustralia: '',
    residencyPostcodeYear12: '',
    residencySuburbYear12: '',
    countryOfResidenceYear12: '',
    parentGuardian1EduLvl: '',
    parentGuardian2EduLvl: '',
    isSecondaryEducationCompleted: '',
    yearLeftSchool: '',
    dateOfBirth: '',
    highestPriorEducationAchieved: '',
    levelLeftSchool: '',
    indigenousOrigin: '',
    stuCitizenCd: '',
    visaQuestionResponse: '',
    visaType: '',
    visaSubType: '',
    isVisaRequired: false,
    isVisaAlreadySubmitted: false,
    isATSIResponded: false
  })
  const [errors, setErrors] = useState({});

  useEffect(()=>{
    //setForm(props.values)
    setForm({...props.values, language: ''})

    Promise.all([
      getHelperTextForPage("Additional Details"),
      getTooltipsForPage("Additional Details"),
      getReferences(),
      getVisaClassDetails(),
      getVisaSubclassDetails(),
    ]).then(
      ([
        helperText,
        tooltips,
        {
          disabilityCodesList,
          soneHighestPriorEducationCodesList,
          soneLevelLeftSchoolList,
          indigenousOriginCodesList,
          citizenshipCodesList,
        },
        soneVisaTypeList,
        soneVisaSubTypeList,
      ]) => {
        setTopCallout(helperText.data.length > 0 ? helperText.data[0].attributes.body.value : topCallout)
        setTooltips(tooltips.data)
        setDisabilities(populateDisabilityOptions(disabilityCodesList))
        setHighestPriorEducationList(populatePriorEducationOptions(soneHighestPriorEducationCodesList))
        setSchoolLevelList(soneLevelLeftSchoolList)
        setInigenousCodesList(indigenousOriginCodesList)
        setCitizenship(getCitizenship(citizenshipCodesList))
        setVisaTypeList(populateVisaTypeOptions(soneVisaTypeList.referenceCodes))
        setVisaSubTypeList(populateVisaSubTypeOptions(soneVisaSubTypeList.referenceCodes))
        setCourseLocationCd(getAttributeCourseStaticInfo("locationCd"))
        setOffshoreFlag(getAttributeCourseStaticInfo("isOffshoreFlag"))
        setLoading(false)
      },
    );
  },[])

  const populateDisabilityOptions = (disabilityCodesList) => {
    let options = [];
    disabilityCodesList.map((item) =>
      options.push({ label: item.explanation, value: item.disabilityType }),
    );
    return options;
  }

  const populateVisaTypeOptions = (visaTypeOptions) => {
    return visaTypeOptions.map((value) => ({
      label: value.description,
      value: value.code,
    }));
  }

  const populateVisaSubTypeOptions = (visaSubTypeOptions) => {
    return visaSubTypeOptions.map((value) => ({
      label: value.description,
      value: value.code,
    }));
  }

  const populatePriorEducationOptions = (priorEducationCodesList) => {
    let options = [];
    priorEducationCodesList.map((item) =>
      options.push({
        label: item.description,
        value: item.highestPriorEducationCode,
      }),
    );
    return options;
  }

  const getCitizenship = (citizenshipCodesList) => {
    const citizenship = citizenshipCodesList.find((citizenship) =>  citizenship.citizenshipCode === props.values.stuCitizenCd);
    if (citizenship) {
      return citizenship.description;
    }
    return null;
  };

  const handleVisaChange = async (e) => {
    const { name, value } = e.target

    try {
      await validationSchema.validateAt(name, { ...form, [name]: value }, { context: form });

      let updateForm = {...form, [name]: value}

      if(name === 'visaQuestionResponse' && form.visaQuestionResponse === 'N') {
        updateForm = {...updateForm, visaType: '', visaSubType: ''};
      } 

      if(name === 'visaType' && value === '$STUDENT') {
        updateForm = {...updateForm, visaSubType: '500'};
      }

      setForm(updateForm);

      setErrors({...errors, [name]: undefined});
    } catch (validationError) {
      setErrors({...errors,[name]: validationError.message});
    }

  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setForm({...form, [name]: value});

    try {
      await validationSchema.validateAt(name, { ...form, [name]: value });
      setErrors({...errors, [name]: undefined});
    } catch (validationError) {
      setErrors({...errors,[name]: validationError.message});
    }
  };

  const handleCountryChange = async (countryFieldName, countryCode) => {
    setForm({...form, [countryFieldName]: countryCode});

    try {
      await validationSchema.validateAt(countryFieldName, { ...form, [countryFieldName]: countryCode }, { context: form });
      setErrors({...errors, [countryFieldName]: undefined});
    } catch (validationError) {
      setErrors({...errors,[countryFieldName]: validationError.message});
    }

  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await validationSchema.validate(form, { context: form, abortEarly: false });
      setErrors({});
      props.handleSubmit(form, true, {})
    } catch (validationErrors) {
      const formattedErrors = validationErrors.inner.reduce((acc, error) => {
        return { ...acc, [error.path]: error.message };
      }, {});
      setErrors(formattedErrors);
      props.handleSubmit(form, false, formattedErrors)
    }
  };


  return loading 
    ? <Loading/> 
    : (
      <div>
        <div className="question-group">
          <HelpText tag={"h3"} text={"Citizenship/Residency Status:"} />
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <TextField
                name="citizenship"
                fullWidth
                value={citizenship}
                disabled
                variant="standard"
              />
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          {form.isVisaRequired && (
            <div>
              {!form.isVisaAlreadySubmitted && (
                <div className="question-group">
                  <HelpText
                    tag={"h3"}
                    text={
                      "Do you have an approved immigration visa issued by the Australian government?"
                    }
                  />

                  <RadioGroup
                    name="visaQuestionResponse"
                    className="visa-question"
                    onChange={handleVisaChange}
                    value={form.visaQuestionResponse}
                  >
                    <FormControlLabel label="Yes" value="Y" control={<Radio/>}/>
                    <FormControlLabel label="No" value="N" control={<Radio/>}/>
                  </RadioGroup>

                  {!!errors.visaQuestionResponse ? (
                    <span className="form-error">
                      {errors.visaQuestionResponse}
                    </span>
                  ) : null}
                </div>
              )}

              {(form.visaQuestionResponse === "Y" ||
                form.isVisaAlreadySubmitted) && (
                <div className="question-group">
                  <div className="row">
                    <div className="col-xs-12 col-md-6">
                      <HelpText tag={"h3"} text={"Visa Class:"} />
                      <FormControl variant="standard" fullWidth>
                        <Select
                          name="visaType"
                          value={form.visaType || ''}
                          onChange={handleVisaChange}
                          disabled={form.isVisaAlreadySubmitted}
                          className={`${form.isVisaAlreadySubmitted ? "select--is-disabled" : ""}`}
                        >
                          {visaTypeList.length &&
                            visaTypeList.map((visa) => (
                              <MenuItem key={visa.value} value={visa.value}>
                                {visa.label}
                              </MenuItem>
                            ))}
                        </Select>
                        {!!errors.visaType ? (
                          <FormHelperText error>
                            {errors.visaType}
                          </FormHelperText>
                        ) : null}
                      </FormControl>
                    </div>
                    <div className="col-xs-12 col-md-6">
                      <HelpText tag={"h3"} text={"Visa Subclass:"} />
                      <FormControl variant="standard" fullWidth>
                        <Select
                          name="visaSubType"
                          value={form.visaSubType || ''}
                          onChange={handleChange}
                          disabled={
                            form.isVisaAlreadySubmitted ||
                            form.visaSubType === "500"
                          }
                          className={`${form.isVisaAlreadySubmitted || form.visaSubType === "500" ? "select--is-disabled" : ""}`}
                        >
                          {visaSubTypeList.length &&
                            visaSubTypeList.map((visa) => (
                              <MenuItem key={visa.value} value={visa.value}>
                                {visa.label}
                              </MenuItem>
                            ))}
                        </Select>
                        {!!errors.visaSubType ? (
                          <FormHelperText error>
                            {errors.visaSubType}
                          </FormHelperText>
                        ) : null}
                      </FormControl>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="question-group">
            <div className="calloutBox__content">
              <div className="topCallout">
                {parse(topCallout)}
              </div>
            </div>
          </div>

          <div className="question-group">
            <HelpText
              tag={"h3"}
              text={"Are you an Aboriginal or Torres Strait Islander?"}
            />
            <FormControl variant="standard" fullWidth>
              <Select
                name="indigenousOrigin"
                value={form.indigenousOrigin}
                onChange={handleChange}
              >
                {indigenousCodesList.length &&
                  indigenousCodesList.map(
                    (indigenousOrigin) => (
                      <MenuItem
                        key={indigenousOrigin.indigenousOriginCode}
                        value={indigenousOrigin.indigenousOriginCode}
                      >
                        {indigenousOrigin.description}
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>
          </div>

          <div className="question-group">
            <HelpText
              tag={"h3"}
              text={
                "Do you have a disability, impairment or medical condition?"
              }
              toolTip={getTooltipBySlug("disability-question",tooltips)}
            />

            <ExpandableRadioGroup
              errorText={
                !!errors.disabilityIndicator
                  ? errors.disabilityIndicator
                  : false
              }
              name="disabilityIndicator"
              onChange={handleChange}
              valueSelected={form.disabilityIndicator}
            >
              <ExpandableRadioButton label="No" value="N" />
              <ExpandableRadioButton label="Yes" value="Y">
                <div>
                  <div className="row">
                    <div className="col-xs-12 col-md-6 question-subgroup">
                      <h3>Please specify</h3>
                      <FormControl variant="standard" fullWidth>
                        <Select
                          name="disability"
                          value={form.disability}
                          multiple
                          options={disabilities}
                          onChange={handleChange}
                        >
                          {disabilities.length &&
                            disabilities.map((disability) => (
                              <MenuItem
                                key={disability.value}
                                value={disability.value}
                              >
                                {disability.label}
                              </MenuItem>
                            ))}
                        </Select>
                        {!!errors.disability ? (
                          <FormHelperText htmlFor="contact-relationship-select" error>
                            {errors.disability}
                          </FormHelperText>
                        ) : (
                          false
                        )}
                      </FormControl>
                    </div>

                    <div className="col-xs-12 question-subgroup">
                      <HelpText
                        tag="H3"
                        text="Would you like to be contacted regarding your disability/s?"
                        toolTip={getTooltipBySlug("disability-contact",tooltips)}
                      />
                      <div className="form-items-group">
                        <RadioGroup
                          name="contactInd"
                          onChange={handleChange}
                          value={form.contactInd}
                        >
                          <FormControlLabel label="No" value="N" control={<Radio/>} />
                          <FormControlLabel label="Yes" value="Y" control={<Radio/>}/>
                        </RadioGroup>
                        {!!errors.contactInd ? (
                          <div className="form-error">{errors.contactInd}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </ExpandableRadioButton>
            </ExpandableRadioGroup>
          </div>
          <div className="question-group">
            <HelpText tag="H3" text="In which country were you born?" />
            <div className="row">
              <div className="col-xs-12 col-md-6">
                <Country
                  errors={!!errors.countryOfBirth}
                  helperText={errors.countryOfBirth}
                  name="countryOfBirth"
                  onChange={handleCountryChange}
                  value={form.countryOfBirth}
                />
              </div>
              {offshoreFlag === "Y" || courseLocationCd === "ZO" || (form.countryOfBirth && form.countryOfBirth === CountryEnum.Australia) ? null : (
                <div className="col-xs-12 col-md-6">
                  <TextField
                    name="yearOfArrival"
                    error={!!errors.yearOfArrival}
                    helperText={errors.yearOfArrival}
                    label="Year arrived in Australia"
                    fullWidth
                    onChange={handleChange}
                    value={form.yearOfArrival}
                    variant="standard"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="question-group">
            <HelpText
              tag="H3"
              text="Do you speak a language other than English at home?"
            />

            <ExpandableRadioGroup
              errorText={
                !!errors.englishOnlyAtHome
                  ? errors.englishOnlyAtHome
                  : false
              }
              name="englishOnlyAtHome"
              onChange={handleChange}
              valueSelected={form.englishOnlyAtHome}
            >
              <ExpandableRadioButton label="English only" value="Y" />
              <ExpandableRadioButton
                label="I speak another language at home"
                value="N"
              >
                <div className="row">
                  <div className="col-xs-12 col-md-6">
                    <Language
                      name="language"
                      errors={errors.language}
                      onChange={handleChange}
                      value={form.language}
                    />
                  </div>
                </div>
              </ExpandableRadioButton>
            </ExpandableRadioGroup>
          </div>
          <div className="question-group">
            <HelpText
              tag="H3"
              text="Where were you living during your final year of Secondary/High School?"
            />

            <ExpandableRadioGroup
              errorText={
                !!errors.year12InAustralia
                  ? errors.year12InAustralia
                  : false
              }
              name="year12InAustralia"
              onChange={handleChange}
              valueSelected={form.year12InAustralia}
            >
              <ExpandableRadioButton label="Australia" value="Y">
                <div className="row">
                  <div className="col-xs-12 col-md-6">
                    <TextField
                      name="residencyPostcodeYear12"
                      error={!!errors.residencyPostcodeYear12}
                      helperText={errors.residencyPostcodeYear12}
                      label="Postcode"
                      fullWidth
                      onChange={handleChange}
                      value={form.residencyPostcodeYear12}
                      variant="standard"
                    />
                  </div>
                  <div className="col-xs-12 col-md-6">
                    <TextField
                      name="residencySuburbYear12"
                      error={!!errors.residencySuburbYear12}
                      helperText={errors.residencySuburbYear12}
                      label="Suburb"
                      fullWidth
                      maxLength={27}
                      onChange={handleChange}
                      value={form.residencySuburbYear12}
                      variant="standard"
                    />
                  </div>
                </div>
              </ExpandableRadioButton>
              <ExpandableRadioButton label="Overseas" value="N">
                <div className="row">
                  <div className="col-xs-12 col-md-6">
                    <Country
                      name="countryOfResidenceYear12"
                      errors={!!errors.countryOfResidenceYear12}
                      helperText={errors.countryOfResidenceYear12}
                      onChange={handleCountryChange}
                      value={form.countryOfResidenceYear12}
                      allowedCountries="OVERSEAS"
                    />
                  </div>
                </div>
              </ExpandableRadioButton>
            </ExpandableRadioGroup>
          </div>
          <div className="question-group">
            <HelpText
              tag="H3"
              text="What was the highest level of education completed by your Parents/Guardians?"
              toolTip={getTooltipBySlug("highest-level-edu",tooltips)}
            />
            <div className="formGroup">
              <div className="row">
                <div>
                  <h4>Parent/Guardian 1</h4>
                  <ParentEducationLevel
                    name="parentGuardian1EduLvl"
                    errors={!!errors.parentGuardian1EduLvl}
                    helperText={errors.parentGuardian1EduLvl}
                    onChange={handleChange}
                    value={form.parentGuardian1EduLvl}
                  />
                </div>
                <div>
                  <h4>Parent/Guardian 2</h4>
                  <ParentEducationLevel
                    name="parentGuardian2EduLvl"
                    onChange={handleChange}
                    value={form.parentGuardian2EduLvl}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="question-group">
            <h3>
              What was your highest prior education achieved before this course?
            </h3>
            <div className="formGroup">
              <div className="row">
                <div className="col-xs-12">
                  <FormControl variant="standard" fullWidth>
                    <Select
                      className="dropdown"
                      name="highestPriorEducationAchieved"
                      value={form.highestPriorEducationAchieved}
                      options={highestPriorEducationList}
                      onChange={handleChange}
                    >
                      {highestPriorEducationList.length &&
                        highestPriorEducationList.map((hpe) => (
                          <MenuItem key={hpe.value} value={hpe.value}>
                            {hpe.label}
                          </MenuItem>
                        ))}
                    </Select>
                    {!!errors.highestPriorEducationAchieved ? (
                      <FormHelperText
                        htmlFor="highestPriorEducationAchieved"
                        error
                      >
                        {errors.highestPriorEducationAchieved}
                      </FormHelperText>
                    ) : (
                      false
                    )}
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
          <div className="question-group">
            <HelpText
              tag="H3"
              text="Have you attended Secondary/High School?"
            />
            <div className="radio-expand-group">
              <ExpandableRadioGroup
                errorText={
                  !!errors.isSecondaryEducationCompleted
                    ? errors.isSecondaryEducationCompleted
                    : false
                }
                name="isSecondaryEducationCompleted"
                onChange={handleChange}
                valueSelected={form.isSecondaryEducationCompleted}
              >
                <ExpandableRadioButton label="No" value="1" />
                <ExpandableRadioButton label="Yes" value="2">
                  <div className="formGroup">
                    <div className="row">
                      <div className="col-xs-12">
                        <h3>Which level did you complete school?</h3>
                        <div className='question-subgroup'>
                          <FormControl variant="standard" fullWidth>
                            <Select
                              name="levelLeftSchool"
                              value={form.levelLeftSchool}
                              onChange={handleChange}
                            >
                              {schoolLevelList.length &&
                                schoolLevelList.map((schoolLevel) => 
                                  <MenuItem
                                    key={schoolLevel.schoolLevelCode}
                                    value={schoolLevel.schoolLevelCode}
                                  >
                                    {schoolLevel.description}
                                  </MenuItem>
                                )
                              }
                            </Select>
                            {!!errors.levelLeftSchool ? (
                              <FormHelperText htmlFor="levelLeftSchool" error>
                                {errors.levelLeftSchool}
                              </FormHelperText>
                            ) : (
                              false
                            )}
                          </FormControl>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12 col-md-6">
                        <TextField
                          name="yearLeftSchool"
                          error={!!errors.yearLeftSchool}
                          helperText={errors.yearLeftSchool}
                          label="Last year of study in Secondary/High School?"
                          fullWidth
                          onChange={handleChange}
                          value={form.yearLeftSchool}
                          variant="standard"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableRadioButton>
              </ExpandableRadioGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 col-md-6">
              {props.saving ? (
                <button className="btn btn-lg btn-primary" disabled>
                  Submitting...
                </button>
              ) : (
                <button className="btn btn-lg btn-primary">
                  Submit &amp; continue
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    );
  
}
export default AdditionalDetailsForm;
